// axisInfo.js — one source of truth for what each lens / axis MEASURES, reused by
// the metric-dropdown (i) explainer, the table header tooltips, and any wiki drawer.
// Plain, writer-facing language — no chart-mechanic jargon.
//
// UPSTREAM SOURCE OF TRUTH: eval/prompts/der_unit_grader_v0_1.txt:43-74 — the text the
// grader model actually receives at scoring time. This file must SIMPLIFY that text, never
// contradict it. See docs/CODEBASE_MAP.md §13.14b / landmine 75 for the four definition
// surfaces and which one wins.
//
// Resynced 2026-08-13 for pressure / development / movement, which still carried pre-patch
// wording from before the PC-01 pressure patch and the movement/development firewall patch:
//   pressure    — "a locatable, page-visible force" omitted the COST-BEARING requirement, so
//                 it read as any driving force including a felt one. Firewall vs charge.
//   development — "Does the thread CHANGE" is MOVEMENT's question. The grader now defines
//                 development as DEEPENS, "not merely a changed result" (:49).
//   movement    — "a real entry→exit change" omitted the same-variable test and the
//                 in-unit-causal-beat requirement, so it credited any activity (:52-54).

export const AXIS_INFO = {
  overall: {
    label: 'Triage',
    lens: 'overview',
    // Corrected 2026-08-13. This previously carried the `de` entry's description verbatim — fill =
    // Execution, border = Design, with a numeric red/amber/green scale. That is the PRE-BAND
    // behaviour and contradicts prepareChartData.js:110-113, which colours this metric from
    // bandOf() → BAND_FILL. Triage is categorical, not a score; see verdictModel.js.
    measures: 'The wheel’s default read: how much work each unit needs, not how it scores. Design, Execution and Read are each rated solid, soft or broken, and the band is the most optimistic call none of them vetoes.',
    scale: 'Keep · Polish · Rework · Rebuild (scene and sequence grain say “Low on its own”, since a unit graded in isolation can’t support a cut-or-combine claim).',
  },
  de: {
    label: 'Design & Execution',
    lens: 'overview',
    measures: 'The legacy two-channel look: the border shows Design (is it built right), the fill shows Execution (does it play on the page).',
    scale: '0–10 each. Border = Design, fill = Execution.',
  },
  design: {
    label: 'Design', lens: 'Design',
    measures: 'Is the unit built right? The sum of contract, pressure, development, movement, and consequence — the structural bones.',
    scale: '0–10. 5–6 functional, 7+ strong.',
  },
  execution: {
    label: 'Execution', lens: 'Execution',
    measures: 'Does the rendering deliver it? The sum of economy, assembly, and clarity — the craft on the page.',
    scale: '0–10. 5–6 functional, 7+ strong.',
  },
  read: {
    label: 'Involvement', lens: 'Involvement',
    measures: 'Does it pop? The sum of freshness, unpredictability, charge, and grip — the felt experience.',
    scale: '0–10. Scored conservatively — a 7+ is a real spark.',
  },
  contract: { label: 'Contract', lens: 'Design', measures: 'Is the unit organized around one clear, committed governing thread — a thing it is actually about?', scale: '0–10' },
  pressure: { label: 'Pressure', lens: 'Design', measures: 'Is there an operative, cost-bearing source GOVERNING the beats — one that can exact a cost? (The source, not the felt effect: a mood or a reported off-page event is charge.)', scale: '0–10' },
  development: { label: 'Development', lens: 'Design', measures: 'Does the thread DEEPEN — gain a new tactic, complication, leverage, or understanding — rather than merely change its result?', scale: '0–10' },
  movement: { label: 'Movement', lens: 'Design', measures: 'Does the unit’s OWN thread produce a held change — entry and exit as two values of the same variable, made by an in-unit causal beat?', scale: '0–10' },
  consequence: { label: 'Consequence', lens: 'Design', measures: 'Does the result oblige what comes next — a debt, threat, or constraint the next unit must face?', scale: '0–10' },
  economy: { label: 'Economy', lens: 'Execution', measures: 'No avoidable waste — does every line and beat earn its place?', scale: '0–10' },
  assembly: { label: 'Assembly', lens: 'Execution', measures: 'Are the parts well ordered, paced, and seamed together?', scale: '0–10' },
  clarity: { label: 'Clarity', lens: 'Execution', measures: 'Can the intended reader follow what matters (who / what / where / when)?', scale: '0–10' },
  freshness: { label: 'Freshness', lens: 'Involvement', measures: 'Is this a non-rote take on its type, or the version we’ve all seen before?', scale: '0–10' },
  unpredictability: { label: 'Unpredictability', lens: 'Involvement', measures: 'Does it fairly defeat anticipation (a seeded turn), or can you call every beat?', scale: '0–10' },
  charge: { label: 'Charge', lens: 'Involvement', measures: 'Is the emotion EVOKED through the beats, or just stated on the page?', scale: '0–10' },
  grip: { label: 'Grip', lens: 'Involvement', measures: 'At the exit, are you pulled into the next unit — an open question or live threat?', scale: '0–10' },
};
